import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { isActiveLuxuryPlusMember } from 'loyalty/selectors/featureToggle'
import ImageHero from 'components/Common/ImageHero'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import LuxuryPlusLogo from 'loyalty/components/LuxuryPlusLogo'
import LuxuryPlusOfferCarousels from 'loyalty/components/LuxuryPlusOfferCarousels'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'loyalty/constants/base'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { fetchProductSubscription } from 'actions/LoyaltyActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { generateLuxuryPlusSubscriptionItems } from 'lib/checkout/luxuryPlusSubscription/cart'
import { rem } from 'polished'
import { isLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import LinePlayIcon from 'components/Luxkit/Icons/line/LinePlayIcon'
import LuxPlusLandingPageBenefit from 'loyalty/components/LuxPlusLandingPageBenefit'
import Faq from 'components/Common/Faq'
import PaneBody from 'components/Common/Pane/PaneBody'
import LuxuryPlusSubscriptionTsAndCs from 'loyalty/components/LuxPlusSubscriptionTsAndCs'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import { themeClassName } from 'lib/theme/themeUtils'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import CurrencyContext from 'contexts/currencyContext'

const BenefitsSection = styled(Pane)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(60)};
`

interface MappedProps {
  isActiveLuxuryPlusMember: boolean;
  firstName: string;
  currentRegionCode: string;
}

interface Props extends MappedProps {}

interface VideoConfigData {
  id: string;
  query: string;
}

const regionVideoConfig: Record<string, VideoConfigData> = {
  NZ: {
    id: '951826524',
    query: '?h=e5a62b04b9',
  },
  AU: {
    id: '960860398',
    query: '?h=989d9fbb92',
  },
}

function LuxuryPlusLandingPage({ isActiveLuxuryPlusMember, firstName, currentRegionCode }: Props) {
  const currency = useContext(CurrencyContext)
  const dispatch = useAppDispatch()
  const subscriptionProduct = useAppSelector(state => state.loyalty.products.subscription)
  const isLoggedInUser = useAppSelector(isLoggedIn)
  // when we're spoofing (admin/cs is in control), we allow joining up without a join fee
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  const showModal = useContext(ModalContext)
  const showLightbox = useContext(LightBoxContext)

  const openLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const videoConfig = regionVideoConfig[currentRegionCode]

  const openLuxPlusPromotionalVideo = useCallback(() => {
    showLightbox(<VideoLightbox videoId={videoConfig.id} query={videoConfig.query} playOnMount />)
  }, [videoConfig.id, videoConfig.query, showLightbox])

  const [offer, joinFeeFreeOffer] = useMemo(() => [
    subscriptionProduct.offers?.find(offer => !offer.joinFeeWaived),
    subscriptionProduct.offers?.find(offer => offer.joinFeeWaived),
  ], [subscriptionProduct.offers])

  const onProceedToCheckout = useCallback((offer: App.SubscriptionOffer) => {
    const cartItems = generateLuxuryPlusSubscriptionItems(offer)
    fireEcommerceGTMEvent(addToCartGTM(offer, currency, offer.price + offer.joinFee, undefined, 'subscription'))
    dispatch(addToCheckoutCart(cartItems))
    dispatch(navigateToCheckout())
  }, [currency, dispatch])

  const onJoin = useCallback(() => {
    if (offer) {
      onProceedToCheckout(offer)
    }
  }, [offer, onProceedToCheckout])

  const onJoinNoFee = useCallback(() => {
    if (joinFeeFreeOffer) {
      onProceedToCheckout(joinFeeFreeOffer)
    }
  }, [joinFeeFreeOffer, onProceedToCheckout])

  const heading = useMemo(() => {
    if (isActiveLuxuryPlusMember) return `${firstName ? `${firstName}, ` : ''}welcome to the VIP travel club!`

    return 'Your VIP travel club'
  }, [firstName, isActiveLuxuryPlusMember])

  return <>
    <ImageHero
      fixedHeight
      className={themeClassName('inverse')}
      id="wm1k0iah4b8lgljuwte"
      alt=""
      aria-hidden="true"
      brightness={-25}
    >
      <LayoutContainer>
        <VerticalSpacer gap={16}>
          <LuxuryPlusLogo height={20} />
          <VerticalSpacer gap={4}>
            <Heading variant="heading1" colour="neutral-one" format="propercase">{heading}</Heading>
            <Heading variant="heading4" colour="neutral-one">Unpack a world of member-only benefits</Heading>
          </VerticalSpacer>
          {isActiveLuxuryPlusMember &&
            <TextButton
              kind="primary"
              size="medium"
              to={`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`}
              fit="mobile-full-width"
            >
              Manage membership
            </TextButton>}
          {!isActiveLuxuryPlusMember && <>
            <Group direction="vertical" tabletDirection="horizontal" gap={16}>
              {videoConfig && <TextButton
                kind="secondary"
                startIcon={<LinePlayIcon />}
                fit="mobile-full-width"
                onClick={openLuxPlusPromotionalVideo}
              >
                Watch video
              </TextButton>}
              <TextButton
                kind="primary"
                disabled={!subscriptionProduct.offers}
                onClick={onJoin}
                fit="mobile-full-width"
              >
                Become a {LUXURY_PLUS.PROGRAM_NAME} member
              </TextButton>
              {isSpoofed && joinFeeFreeOffer &&
                <TextButton
                  kind="primary"
                  disabled={!subscriptionProduct.offers}
                  onClick={onJoinNoFee}
                  fit="mobile-full-width"
                >
                  Become a {LUXURY_PLUS.PROGRAM_NAME} member (no joining fee)
                </TextButton>}
            </Group>
            {!isLoggedInUser && <BodyText variant="medium" colour="neutral-one">
              Already a {LUXURY_PLUS.PROGRAM_NAME} member? <TextLink size="medium" onClick={openLogin}>Log in</TextLink>
            </BodyText>}
          </>}
        </VerticalSpacer>
      </LayoutContainer>
    </ImageHero>

    <BenefitsSection type="loyalty" className={themeClassName('inverse')}>
      <LayoutContainer>
        {subscriptionProduct.fetching && <LoadingIndicator inline>Loading...</LoadingIndicator>}
        {offer && <VerticalSpacer gap={40}>
          <VerticalSpacer gap={8}>
            <Heading variant="heading3" colour="neutral-one">Unlimited benefits all year round</Heading>
            {!isActiveLuxuryPlusMember && <>
              <BodyText variant="medium" colour="neutral-one">
                As a member of the {LUXURY_PLUS.PROGRAM_NAME} VIP travel club you'll enjoy unlimited benefits{' '}
                all year round for <FormatCurrency value={offer.price} />/year, and a one-off joining fee of <FormatCurrency value={offer.joinFee} />.
              </BodyText>
              <BodyText variant="medium" weight="bold" colour="highlight-primary">
                Special offer: No joining fee when you purchase an eligible {LUXURY_PLUS.PROGRAM_NAME} offer (save <FormatCurrency value={offer.joinFee} />).
              </BodyText>
            </>}
          </VerticalSpacer>
          <CardCarousel width={rem(200)} pageSize={6} gap={20} snap="end">
            {offer.benefits.map((benefit) => (
              <LuxPlusLandingPageBenefit key={benefit.title} benefit={benefit} />
            ))}
          </CardCarousel>
          <LuxuryPlusSubscriptionTsAndCs />
        </VerticalSpacer>}
      </LayoutContainer>
    </BenefitsSection>
    <Pane>
      <PaneBody size="xl-y">
        <VerticalSpacer gap={40} >
          <LuxuryPlusOfferCarousels analyticsPage="LUXPLUS" />
          <LayoutContainer>
            <Heading variant="heading3" align="center" as="h2">Frequently Asked Questions</Heading>
            <Faq slug="luxplus-faqs" />
          </LayoutContainer>
        </VerticalSpacer>
      </PaneBody>
    </Pane>
  </>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  isActiveLuxuryPlusMember: isActiveLuxuryPlusMember(state),
  firstName: state.auth.account.givenName,
  currentRegionCode: state.geo.currentRegionCode,
})

export default connect(mapStateToProps)(LuxuryPlusLandingPage)
